import type {
  BudgetReportResponse,
  OrganisationMonthlyBudgetResponse,
  OrganisationTrendMonthResponse,
  OrganisationTrendResponse,
} from "../../api/organisationReporting";

/**
 * The month RWS's financial year starts in: October, so FY27 runs from October 2026 to September 2027. A year is named by the
 * calendar year it ends in.
 */
export const financialYearStartMonth = 10;

/** How many months the charts show: the last twelve, whatever the financial year. */
export const chartMonths = 12;

/** The label a unit carries when its spend cannot be placed in the organisation chart (`CostCentreDeriver.Unclassified`). */
export const unclassifiedLabel = "Unclassified";

/** The headline figures for the month shown and the run of months before it. */
export interface OverviewHeadline {
  /** The month shown: the last of the trend. */
  month: OrganisationTrendMonthResponse;
  /** The month before it, or null when the trend holds only one. */
  previous: OrganisationTrendMonthResponse | null;
  /** The month's spend so far, or its whole spend once it has ended. */
  spend: number;
  /** Where the running month is heading, or null for a month that has ended or when there is no projection. */
  projected: number | null;
  /** The change against the month before, as a percentage of it: the projection's on a running month. Null with nothing to compare. */
  changePercent: number | null;
  /** The spend over the last {@link chartMonths} months, the shown month among them. */
  periodTotal: number;
  /** Of the month before's active people, the share active again this month, as a percentage; null with nothing to compare. */
  returningPercent: number | null;
}

/**
 * The headline: the month shown, where it is heading, how it compares with the month before, and the totals over the run of
 * months. A running month is compared on its projection, not on its spend so far, which would always look like a fall.
 *
 * @param trend - The trend, oldest month first.
 * @param projectedTotal - The running month's projected total, from the daily spend, or null.
 * @returns The headline, or null when the trend holds no months.
 */
export function overviewHeadline(trend: OrganisationTrendResponse, projectedTotal: number | null): OverviewHeadline | null {
  const month = trend.months[trend.months.length - 1] as OrganisationTrendMonthResponse | undefined;
  if (month === undefined) {
    return null;
  }

  const previous = trend.months.length > 1 ? trend.months[trend.months.length - 2] : null;
  const spend = month.totalSpend ?? 0;
  const projected = month.isCurrentMonth && projectedTotal !== null ? Math.max(projectedTotal, spend) : null;
  const compared = projected ?? spend;
  const previousSpend = previous?.totalSpend ?? 0;

  return {
    month,
    previous,
    spend,
    projected,
    changePercent: previousSpend > 0 ? ((compared - previousSpend) / previousSpend) * 100 : null,
    periodTotal: trend.months.slice(-chartMonths).reduce((sum, candidate) => sum + (candidate.totalSpend ?? 0), 0),
    returningPercent:
      previous !== null && previous.activePeople > 0 && month.returningPeople !== null ? (month.returningPeople / previous.activePeople) * 100 : null,
  };
}

/** A tool's adoption in one month: how many people had it, and how many of them used it. */
export interface SeatUse {
  vendorId: string;
  displayName: string;
  /** People who had the tool: a member of its account, or anyone who paid for or used it. */
  allocatedPeople: number;
  activePeople: number;
  /** The share of people with the tool who used it, as a percentage, at most 100; null when nobody had it. */
  usedPercent: number | null;
  /** What the paying people who did not use the tool cost: seat fees for nothing. */
  idleSpend: number;
}

/**
 * Each tool's given and used counts for one month, for the tools anyone had. The difference is people given the tool who did not
 * use it: over-allocation or slow adoption for every tool, and a real cost as well where the tool is billed by the seat.
 *
 * @param trend - The trend, for the tools' names and order.
 * @param month - The month.
 * @returns One entry per tool anyone had, in the trend's order.
 */
export function seatUse(trend: OrganisationTrendResponse, month: OrganisationTrendMonthResponse): SeatUse[] {
  return trend.vendors
    .map((vendor) => {
      const figures = month.vendors.find((candidate) => candidate.vendorId === vendor.vendorId);
      const activePeople = figures?.activePeople ?? 0;
      const allocatedPeople = Math.max(figures?.allocatedPeople ?? 0, activePeople);
      return {
        vendorId: vendor.vendorId,
        displayName: vendor.displayName,
        allocatedPeople,
        activePeople,
        usedPercent: allocatedPeople > 0 ? Math.min(100, (activePeople / allocatedPeople) * 100) : null,
        idleSpend: figures?.idleSpend ?? 0,
      };
    })
    .filter((use) => use.allocatedPeople > 0);
}

/** A point on the run of months where a tool's figures begin: before it, the tool's rise is its recording starting, not its use growing. */
export interface CoverageStart {
  vendorId: string;
  displayName: string;
  periodStart: string;
}

/**
 * Where each tool's spend (or use) begins within the trend, for the tools whose figures start after its first month. A tool whose
 * figures are there from the first month is left out: nothing in the run of months says when its recording began.
 *
 * @param trend - The trend.
 * @param kind - Whether to read when spend or when use begins.
 * @returns One entry per tool whose figures begin inside the run, earliest first.
 */
export function coverageStarts(trend: OrganisationTrendResponse, kind: "spend" | "activity"): CoverageStart[] {
  const first = trend.months[0]?.periodStart;
  return trend.vendors
    .map((vendor) => ({
      vendorId: vendor.vendorId,
      displayName: vendor.displayName,
      periodStart: (kind === "spend" ? vendor.firstMonthWithSpend : vendor.firstMonthWithActivity) ?? "",
    }))
    .filter((start) => first !== undefined && start.periodStart !== "" && start.periodStart > first)
    .sort((a, b) => a.periodStart.localeCompare(b.periodStart));
}

/**
 * A month's short label for a chart's axis: the month, with the year where it changes (the first month, and every January).
 *
 * @param periodStart - The month's first day, `YYYY-MM-DD`.
 * @param withYear - Whether to add the year.
 * @returns The label, for example `"Jan 2026"` or `"Feb"`.
 */
export function monthLabel(periodStart: string, withYear: boolean): string {
  return new Date(`${periodStart}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: withYear ? "numeric" : undefined, timeZone: "UTC" });
}

/**
 * The first day of the financial year containing a month.
 *
 * @param periodStart - The month's first day, `YYYY-MM-DD`.
 * @returns The financial year's first day, `YYYY-MM-DD`.
 */
export function financialYearStart(periodStart: string): string {
  const year = Number(periodStart.slice(0, 4));
  const month = Number(periodStart.slice(5, 7));
  const startYear = month >= financialYearStartMonth ? year : year - 1;
  return `${String(startYear)}-${String(financialYearStartMonth).padStart(2, "0")}-01`;
}

/**
 * A financial year's short name, from its first day: the calendar year it ends in, for example `"FY27"` for October 2026 on.
 *
 * @param yearStart - The financial year's first day, `YYYY-MM-DD`.
 * @returns The name.
 */
export function financialYearName(yearStart: string): string {
  // The year starts after January, so it ends in the next calendar year, which names it.
  return `FY${String(Number(yearStart.slice(0, 4)) + 1).slice(-2)}`;
}

/**
 * A month a number of months from another.
 *
 * @param periodStart - The month's first day, `YYYY-MM-DD`.
 * @param months - How many months to move, back when negative.
 * @returns The other month's first day, `YYYY-MM-DD`.
 */
export function addMonths(periodStart: string, months: number): string {
  const date = new Date(`${periodStart}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
}

/**
 * How many months the overview asks the trend for, so that it holds the whole of the previous financial year as well as this one
 * up to the shown month: at least {@link chartMonths}, at most 24 (two financial years).
 *
 * @param periodStart - The shown month's first day.
 * @returns The number of months.
 */
export function monthsToRequest(periodStart: string): number {
  const previousStart = addMonths(financialYearStart(periodStart), -12);
  const months = (Number(periodStart.slice(0, 4)) - Number(previousStart.slice(0, 4))) * 12 + Number(periodStart.slice(5, 7)) - Number(previousStart.slice(5, 7)) + 1;
  return Math.max(chartMonths, months);
}

/** This financial year so far against the same point of the last one, and the last one in full. */
export interface FinancialYearComparison {
  /** This year's name, for example `"FY27"`. */
  name: string;
  /** The last year's name. */
  previousName: string;
  /** This year's spend so far, the shown month included. */
  soFar: number;
  /** How many months of this year are counted, the shown month included. */
  monthsIn: number;
  /** The last year's spend over the same months, or null when the trend does not reach back to them. */
  previousSamePoint: number | null;
  /** The last year's whole spend, or null when the trend does not hold all of it. */
  previousTotal: number | null;
  /**
   * The change on the same point of the last year, as a percentage of it; null with nothing to compare, and null when the last
   * year is incomplete ({@link incompleteFrom}), where the whole-year change would measure the recording, not the use.
   */
  changePercent: number | null;
  /** The tools whose spend began to be recorded part-way through the last year or later, which make the comparison unfair. */
  incompleteFrom: CoverageStart[];
  /**
   * When the last year is incomplete, the change over only the tools recorded through both years, or null when there are none
   * or they spent nothing at the same point of the last year.
   */
  likeForLike: { tools: string[]; soFar: number; previousSamePoint: number; changePercent: number } | null;
}

/**
 * This financial year so far against the same months of the last one, and the last one in full. A tool whose spend was recorded
 * only part-way through the last year makes the comparison overstate growth, so those tools are listed with it.
 *
 * @param trend - The trend, reaching back to the start of the previous financial year.
 * @returns The comparison, or null when the trend holds no months.
 */
export function financialYearComparison(trend: OrganisationTrendResponse): FinancialYearComparison | null {
  const shown = trend.months[trend.months.length - 1] as OrganisationTrendMonthResponse | undefined;
  if (shown === undefined) {
    return null;
  }

  const yearStart = financialYearStart(shown.periodStart);
  const previousStart = addMonths(yearStart, -12);
  const spendBetween = (from: string, to: string): number =>
    trend.months.filter((month) => month.periodStart >= from && month.periodStart <= to).reduce((sum, month) => sum + (month.totalSpend ?? 0), 0);
  const reaches = (periodStart: string): boolean => trend.months.some((month) => month.periodStart <= periodStart);

  const monthsIn = trend.months.filter((month) => month.periodStart >= yearStart).length;
  const soFar = spendBetween(yearStart, shown.periodStart);
  const previousEnd = addMonths(shown.periodStart, -12);
  const previousSamePoint = reaches(previousStart) ? spendBetween(previousStart, previousEnd) : null;
  const previousTotal = reaches(previousStart) ? spendBetween(previousStart, addMonths(yearStart, -1)) : null;
  const incompleteFrom = trend.vendors
    .filter((vendor) => vendor.firstMonthWithSpend !== null && vendor.firstMonthWithSpend > previousStart)
    .map((vendor) => ({ vendorId: vendor.vendorId, displayName: vendor.displayName, periodStart: vendor.firstMonthWithSpend ?? "" }))
    .sort((a, b) => a.periodStart.localeCompare(b.periodStart));

  // Like for like: only the tools whose spend was recorded from the start of the last year, in both years' same months.
  const complete = trend.vendors.filter((vendor) => vendor.firstMonthWithSpend !== null && vendor.firstMonthWithSpend <= previousStart);
  const toolSpendBetween = (from: string, to: string): number =>
    trend.months
      .filter((month) => month.periodStart >= from && month.periodStart <= to)
      .flatMap((month) => month.vendors)
      .filter((figures) => complete.some((vendor) => vendor.vendorId === figures.vendorId))
      .reduce((sum, figures) => sum + (figures.spend ?? 0), 0);
  const likeSoFar = toolSpendBetween(yearStart, shown.periodStart);
  const likePrevious = toolSpendBetween(previousStart, previousEnd);

  return {
    name: financialYearName(yearStart),
    previousName: financialYearName(previousStart),
    soFar,
    monthsIn,
    previousSamePoint,
    previousTotal,
    changePercent:
      previousSamePoint !== null && previousSamePoint > 0 && incompleteFrom.length === 0 ? ((soFar - previousSamePoint) / previousSamePoint) * 100 : null,
    incompleteFrom,
    likeForLike:
      incompleteFrom.length > 0 && previousSamePoint !== null && complete.length > 0 && likePrevious > 0
        ? {
            tools: complete.map((vendor) => vendor.displayName),
            soFar: likeSoFar,
            previousSamePoint: likePrevious,
            changePercent: ((likeSoFar - likePrevious) / likePrevious) * 100,
          }
        : null,
  };
}

/** One period's spend against its budget, for a gauge. */
export interface BudgetPeriod {
  budget: number;
  spent: number;
  /** Where the period is heading, or null when there is no projection. */
  projected: number | null;
  /** How much of the period has passed, as a percentage. */
  elapsedPercent: number;
}

/** The month's spend split into Opex and Capex, for the overview's hero. */
export interface CapexSplit {
  /** The month's spend that is Capex: the Capex spend of every top-level unit, each person counted once. */
  capexUsd: number;
  /** The rest of the month's spend. */
  opexUsd: number;
}

/**
 * The month's Opex and Capex spend, but only when the system setting leaves Capex out of what is set against budgets: only then does the
 * overview say how the spend divides, since otherwise a budget is simply set against all of it. Read from the month's budget report: a
 * unit's Capex spend includes everything beneath it, so the top-level units' figures add up to the organisation's, with nobody counted
 * twice. The Opex part is what is left of the month's headline spend, so the two always add up to the figure the hero shows.
 *
 * @param report - The month's budget report, or null when it could not be read.
 * @param totalSpend - The month's total spend, as the hero shows it.
 * @returns The split, or null when Capex counts against budgets or the report is not to hand.
 */
export function capexSplit(report: BudgetReportResponse | null, totalSpend: number): CapexSplit | null {
  if (report?.capexCountsAgainstBudget !== false || !Array.isArray(report.units)) {
    return null;
  }

  const capexUsd = report.units.filter((unit) => unit.parentId === null).reduce((sum, unit) => sum + unit.capexSpendUsd, 0);
  return { capexUsd, opexUsd: Math.max(0, totalSpend - capexUsd) };
}

/**
 * This month and this financial year against the budgets recorded for them (GH-135). Each month's budget is the organisation's: the
 * sum of the top-level units' budgets, explicit or implied, as they stood in that month. The year's budget adds up the budgets of its
 * twelve months, so a budget changed or back-dated part way through counts for the months it applied to; a month still to come
 * counts the budgets as they stand now. The year is projected at this month's rate: what is spent, the rest of this month as
 * projected, then every month left at this month's projected total.
 *
 * @param headline - The headline figures, for this month.
 * @param financialYear - This financial year so far.
 * @param monthlyBudgets - The organisation's budget for each month of this financial year, or null when they could not be read.
 * @param now - The instant the figures were read at, for how much of each period has passed.
 * @returns The two periods, or null when no budget is recorded for this month.
 */
export function overviewBudget(
  headline: OverviewHeadline,
  financialYear: FinancialYearComparison | null,
  monthlyBudgets: readonly OrganisationMonthlyBudgetResponse[] | null,
  now: Date,
): { month: BudgetPeriod; year: BudgetPeriod | null } | null {
  const monthlyBudget = monthlyBudgets?.find((entry) => entry.periodStart === headline.month.periodStart)?.budgetUsd ?? null;
  if (monthlyBudget === null || monthlyBudget <= 0) {
    return null;
  }

  const monthStart = new Date(`${headline.month.periodStart}T00:00:00Z`);
  const daysInMonth = new Date(Date.UTC(monthStart.getUTCFullYear(), monthStart.getUTCMonth() + 1, 0)).getUTCDate();
  const monthFraction = headline.month.isCurrentMonth ? Math.min(1, Math.max(0, (now.getTime() - monthStart.getTime()) / (daysInMonth * 86400000))) : 1;
  const month: BudgetPeriod = { budget: monthlyBudget, spent: headline.spend, projected: headline.projected, elapsedPercent: monthFraction * 100 };

  if (financialYear === null) {
    return { month, year: null };
  }

  const monthsLeft = 12 - financialYear.monthsIn;
  const monthEnd = headline.projected ?? headline.spend;
  const yearStart = financialYearStart(headline.month.periodStart);
  const yearBudget = (monthlyBudgets ?? [])
    .filter((entry) => entry.periodStart >= yearStart && entry.periodStart < addMonths(yearStart, 12))
    .reduce((sum, entry) => sum + (entry.budgetUsd ?? 0), 0);
  return {
    month,
    year: {
      budget: yearBudget,
      spent: financialYear.soFar,
      projected: headline.projected === null ? null : financialYear.soFar + (monthEnd - headline.spend) + monthEnd * monthsLeft,
      elapsedPercent: ((financialYear.monthsIn - 1 + monthFraction) / 12) * 100,
    },
  };
}
